import React, { useState, useEffect } from 'react';
import { Trophy, Award, Medal, Star, Filter, TrendingUp, Users, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface TopAchiever {
  userId: string;
  achievementCount: number;
  achievements: Array<{
    id: string;
    title: string;
    sport: string;
    type: string;
    level: string;
    year: number;
    achievedAt: string;
    position?: string;
  }>;
  latestAchievement: string;
  user: {
    id: string;
    name: string;
    role: string;
    sport: string;
    avatar: string | null;
    bio?: string;
  };
}

interface EliteAchiever extends TopAchiever {
  eliteAchievementCount: number;
}

const WallOfFamePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [topAchievers, setTopAchievers] = useState<TopAchiever[]>([]);
  const [topEliteAchievers, setTopEliteAchievers] = useState<EliteAchiever[]>([]);
  const [achievementsBySport, setAchievementsBySport] = useState<any[]>([]);
  const [achievementsByYear, setAchievementsByYear] = useState<any[]>([]);
  const [selectedSport, setSelectedSport] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'elite'>('all');

  const sportOptions = [
    'all', 'Football', 'Cricket', 'Tennis', 'Chess', 'Basketball', 
    'Swimming', 'Badminton', 'Athletics', 'Volleyball', 'Hockey', 
    'Boxing', 'Wrestling', 'Gymnastics', 'Cycling'
  ];

  useEffect(() => {
    loadWallOfFame();
  }, [selectedSport, selectedYear]);

  const loadWallOfFame = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedSport !== 'all') params.append('sport', selectedSport.toLowerCase());
      if (selectedYear !== 'all') params.append('year', selectedYear);

      const response = await api.get(`/wall-of-fame?${params.toString()}`);
      
      if (response.data.success) {
        setTopAchievers(response.data.data.topAchievers || []);
        setTopEliteAchievers(response.data.data.topEliteAchievers || []);
        setAchievementsBySport(response.data.data.achievementsBySport || []);
        setAchievementsByYear(response.data.data.achievementsByYear || []);
      }
    } catch (error: any) {
      console.error('Failed to load Wall of Fame:', error);
      toast.error('Failed to load Wall of Fame');
    } finally {
      setLoading(false);
    }
  };

  const getLevelBadgeColor = (level: string) => {
    switch (level) {
      case 'international':
      case 'professional':
        return 'bg-purple-100 text-purple-800';
      case 'national':
        return 'bg-blue-100 text-blue-800';
      case 'regional':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPositionIcon = (position?: string) => {
    if (!position) return null;
    if (position.includes('1') || position.toLowerCase().includes('winner') || position.toLowerCase().includes('gold')) {
      return <Medal className="w-5 h-5 text-yellow-500" />;
    }
    if (position.includes('2') || position.toLowerCase().includes('silver')) {
      return <Medal className="w-5 h-5 text-gray-400" />;
    }
    if (position.includes('3') || position.toLowerCase().includes('bronze')) {
      return <Medal className="w-5 h-5 text-orange-600" />;
    }
    return null;
  };

  const displayAchievers = activeTab === 'all' ? topAchievers : topEliteAchievers;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading Wall of Fame..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Trophy className="w-16 h-16 sm:w-20 sm:h-20" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">Wall of Fame</h1>
            <p className="text-lg sm:text-xl text-yellow-100">
              Celebrating our top achievers and their remarkable accomplishments
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                {sportOptions.map((sport) => (
                  <option key={sport} value={sport}>
                    {sport === 'all' ? 'All Sports' : sport}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="all">All Years</option>
                {achievementsByYear.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item._id}
                  </option>
                ))}
              </select>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'all'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Achievers
              </button>
              <button
                onClick={() => setActiveTab('elite')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'elite'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Elite Achievers
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {displayAchievers.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No achievers found</h3>
            <p className="text-gray-500">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {displayAchievers.map((achiever, index) => (
              <div
                key={achiever.userId}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start space-x-4">
                  {/* Rank Badge */}
                  <div className="flex-shrink-0">
                    {index < 3 ? (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                        index === 0 ? 'bg-yellow-500' :
                        index === 1 ? 'bg-gray-400' :
                        'bg-orange-600'
                      }`}>
                        {index + 1}
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold">
                        {index + 1}
                      </div>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <div
                        className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium cursor-pointer hover:opacity-80"
                        onClick={() => navigate(`/profile/${achiever.userId}`)}
                      >
                        {achiever.user.avatar ? (
                          <img
                            src={achiever.user.avatar}
                            alt={achiever.user.name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          achiever.user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                        )}
                      </div>
                      <div>
                        <h3
                          className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-yellow-600"
                          onClick={() => navigate(`/profile/${achiever.userId}`)}
                        >
                          {achiever.user.name}
                        </h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span>{achiever.user.role}</span>
                          <span>•</span>
                          <span>{achiever.user.sport}</span>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="flex items-center space-x-1 text-yellow-600">
                        <Trophy className="w-4 h-4" />
                        <span className="font-semibold">
                          {activeTab === 'elite' 
                            ? (achiever as EliteAchiever).eliteAchievementCount 
                            : achiever.achievementCount} Achievement{activeTab === 'elite' 
                            ? (achiever as EliteAchiever).eliteAchievementCount !== 1 
                            : achiever.achievementCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* Top Achievements */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Top Achievements:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {achiever.achievements.slice(0, 4).map((achievement) => (
                          <div
                            key={achievement.id}
                            className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  {getPositionIcon(achievement.position)}
                                  <h5 className="font-medium text-sm text-gray-900">
                                    {achievement.title}
                                  </h5>
                                </div>
                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                  <span className={`px-2 py-0.5 rounded-full ${getLevelBadgeColor(achievement.level)}`}>
                                    {achievement.level}
                                  </span>
                                  <span>{achievement.year}</span>
                                  {achievement.position && (
                                    <>
                                      <span>•</span>
                                      <span>{achievement.position}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WallOfFamePage;

